import { createSelector } from "reselect";
import {
  localeSelector,
  numberFormatSelector,
  deliverySlotsSelector,
  shippingMethodDetailsSelector,
  selfPickupStationDetailsSelector,
  formValuesSelector,
  guestOrderSelector,
  currencyFormatSelector,
  globalConfigSelector
} from "ducks/selectors";
import {
  addressParams,
  constructLocalizedDeliveryAddress,
  constructLocalizedDeliverySlotDetails,
  compactJoin,
  formatContactNumber,
  getFormattedDeliveryDateAndTime,
  constructShippingDetails,
  constructSelfPickupDetails
} from "utils/helpers";
import { formatCurrency } from "utils/i18nFormatter";
import {
  ALL_SHIPMENT_METHOD_ALLOWED,
  SHIPMENT_METHOD,
  SHIPMENT_FAILED_STATUS,
  SIM_TYPES
} from "utils/constants";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { forms } from "utils/constants";

export const rescheduleDeliverySelector = formValuesSelector(
  forms.RESCHEDULE_DELIVERY_INFO_FORM
);

export const makeSelectRescheduleDeliveryForm = () =>
  rescheduleDeliverySelector;

export const makeSelectRescheduleDeliveryFormInitialValues = () =>
  createSelector(
    guestOrderSelector,
    globalConfigSelector,
    (guestOrders, globalConfig) => {
      const selectAddress = "initial_shipment_details";

      const last_delivery_method = get(
        guestOrders,
        `${selectAddress}.deliveryMethod`
      );
      return {
        order: {
          state: get(guestOrders, "state")
        },
        email: get(guestOrders, "order_user_detail.email"),
        delivery_state: get(guestOrders, `${selectAddress}.state`, ""),
        delivery_city: get(guestOrders, `${selectAddress}.city`, ""),
        delivery_country: get(globalConfig.country_config.country, "name", ""),
        delivery_slot_full_data: "",
        // delivery_zip_code:
        //   last_delivery_method === SHIPMENT_METHOD.DOORSTEP_DELIVERY
        //     ? get(guestOrders, `${selectAddress}.postalCode`, "")
        //     : "",
        delivery_street_name: get(
          guestOrders,
          `${selectAddress}.address_line_1`,
          get(guestOrders, `${selectAddress}.addressLine1`, "")
        ),
        delivery_unit_no: get(
          guestOrders,
          `${selectAddress}.address_line_2`,
          get(guestOrders, `${selectAddress}.addressLine2`, "")
        ),
        delivery_address_line_1: get(
          guestOrders,
          `${selectAddress}.address_line_1`,
          get(guestOrders, `${selectAddress}.addressLine1`, "")
        ),
        delivery_address_line_2: get(
          guestOrders,
          `${selectAddress}.address_line_2`,
          get(guestOrders, `${selectAddress}.addressLine2`, "")
        ),
        delivery_self_pickup_zip_code:
          last_delivery_method === SHIPMENT_METHOD.DOORSTEP_DELIVERY
            ? ""
            : get(guestOrders, `${selectAddress}.postalCode`, ""),
        last_delivery_method,
        last_delivery_date: get(guestOrders, `${selectAddress}.slot.date`),
        last_slot_id: get(guestOrders, `${selectAddress}.slot.slotId`),
        last_slot_fee: get(guestOrders, `${selectAddress}.price.price`, ""),
        last_slot_fee_structure: {
          price: get(guestOrders, `${selectAddress}.price.price`),
          currency: get(globalConfig, "currency", "")
        },
        last_self_pick_up_location_id: get(
          guestOrders,
          `${selectAddress}.selfPickUpLocationId`,
          ""
        ),
        slot_fee_currency: get(globalConfig, "currency", ""),
        postpaid: get(guestOrders, "postpaid")
      };
    }
  );

export const rescheduleOrderSelector = () =>
  createSelector(
    guestOrderSelector,
    rescheduleDeliverySelector,
    (order, rescheduleDetails) => {
      let orderInfo = {};
      const userDetailKey = "order_user_detail"; //DECIDED by the manage order API response
      const first_name = get(order, `${userDetailKey}.first_name`);
      const middle_name = get(order, `${userDetailKey}.middle_name`);
      const last_name = get(order, `${userDetailKey}.last_name`);
      const full_name = compactJoin([first_name, middle_name, last_name], " ");
      const contact_number = get(order, `${userDetailKey}.phone_number`);
      const email = get(order, `${userDetailKey}.email`);
      const state = get(order, "state");
      const [dobYear, dobMonth, dobDay] =
        get(order, `${userDetailKey}.dob`, "")?.split("-") || [];

      orderInfo = {
        ...order,
        ...rescheduleDetails,
        full_name,
        middle_name,
        last_name,
        first_name,
        contact_number,
        email,
        state,
        dobDay: {
          value: dobDay
        },
        dobMonth: {
          value: dobMonth
        },
        dobYear: {
          value: dobYear
        }
      };
      return orderInfo;
    }
  );

export const makeSelectRescheduleDeliveryDetailsItems = () =>
  createSelector(
    rescheduleOrderSelector(),
    numberFormatSelector,
    deliverySlotsSelector,
    shippingMethodDetailsSelector,
    selfPickupStationDetailsSelector,
    localeSelector,
    rescheduleDeliverySelector,
    currencyFormatSelector,
    (
      order,
      numberFormat,
      deliverySlots,
      lastShipmentMethod,
      selfPickupStationDetails,
      locale,
      currentFormvalues,
      currencyFormat
    ) => {
      const formData = !isEmpty(order) ? order : {};

      if (isEmpty(formData)) return {};
      const postpaid = get(formData, "postpaid");
      const full_name = get(formData, "full_name");
      const first_name = get(formData, "first_name");
      const middle_name = get(formData, "middle_name");
      const last_name = get(formData, "last_name");
      const name =
        full_name || compactJoin([first_name, middle_name, last_name], " ");
      const countryCode =
        get(formData, "country_code") || get(numberFormat, "country_code");
      const birthDate = get(formData, "dobDay");
      const birthMonth = get(formData, "dobMonth");
      const birthYear = get(formData, "dobYear");
      const dateOfBirth = `${birthDate?.value}/${birthMonth?.value}/${birthYear?.value}`;

      const contact_number = get(formData, "contact_number");
      const contactNumber = formatContactNumber(
        contact_number,
        numberFormat,
        countryCode
      );
      const contactEmail = get(formData, "email");
      const slotDateTime = get(currentFormvalues, "delivery_slot_full_data");
      const deliverySlotLabel = get(currentFormvalues, "delivery_slot_label");

      const slot = constructLocalizedDeliverySlotDetails(slotDateTime, locale);
      const { deliveryDate, deliveryTime } = slot;
      const deliveryDateAndTime = getFormattedDeliveryDateAndTime(
        deliveryDate,
        deliverySlotLabel,
        {},
        locale
      );
      const deliveryAddressDetails = addressParams(
        currentFormvalues,
        "delivery"
      );
      const deliveryAddress = constructLocalizedDeliveryAddress(
        deliveryAddressDetails,
        locale
      );

      const deliverySlotId = get(currentFormvalues, "delivery_slot_id");
      const newDeliveryDate = get(currentFormvalues, "delivery_date");
      const deliveryLandMark = get(currentFormvalues, "delivery_landmark", "");
      const deliverySlot = get(currentFormvalues, "delivery_slot");
      const orderState = get(order, "state");
      // allowedShippingMethod - allowed all methods of delivery methods if
      // 1- If order is Prepaid then shipment will be {initiated and created} right after placing the order i.e. independent of KYC
      // hence user will get next_step as reschedule_delivery only if LASS have DFL status and user will be allowed to access all delivery method very time(PREPAID)
      // 2- POSTPAID- if order state is DFL then only he will be allowed to all the delivery methods

      const simType = get(formData, "simType", get(formData, "sim_type"));
      const allowedShippingMethod =
        !postpaid || SHIPMENT_FAILED_STATUS.includes(orderState)
          ? ALL_SHIPMENT_METHOD_ALLOWED
          : lastShipmentMethod;

      const newShipmentMethod = get(currentFormvalues, "shippingMethod.name");
      const shippingDetails =
        simType === SIM_TYPES.eSIM
          ? get(locale, "door_step_kyc", "Doorstep KYC")
          : constructShippingDetails(
              get(currentFormvalues, "shippingMethod.name"),
              locale
            );
      const selfPickupDetails = constructSelfPickupDetails(
        get(currentFormvalues, "selfPickUpLocation"),
        locale
      );
      const selfPickUpLocation = get(currentFormvalues, "selfPickUpLocation");
      const newSelfPickUpSlotDetails = {
        selfPickUpLocation,
        selfPickupDetails
      };

      const newDeliveryDetails = {
        deliverySlotId,
        deliverySlot,
        deliveryDate,
        newDeliveryDate,
        deliveryTime,
        deliverySlotLabel,
        deliveryDateAndTime,
        deliveryAddressDetails,
        deliveryLandMark,
        deliveryAddress
      };

      const lastDeliveryDetails = {
        last_delivery_date: get(currentFormvalues, "last_delivery_date"),
        last_slot_id: get(currentFormvalues, "last_slot_id"),
        last_slot_fee_structure: get(
          currentFormvalues,
          "last_slot_fee_structure"
        ),
        last_self_pick_up_location_id: get(
          currentFormvalues,
          "last_self_pick_up_location_id"
        )
      };
      const contractType = get(
        formData,
        "contractType",
        get(formData, "contract_type")
      );
      const rescheduleDeliveryFee =
        get(currentFormvalues, "shippingMethod.name") ===
        SHIPMENT_METHOD.SELF_PICKUP
          ? get(currentFormvalues, "selfPickUpLocation.deliveryFee")
          : currentFormvalues?.delivery_slot_fee
          ? get(currentFormvalues, "delivery_slot_fee")
          : 0;
      const formattedDeliveryFee =
        rescheduleDeliveryFee > 0
          ? formatCurrency(rescheduleDeliveryFee, { style: "short" })
          : 0;

      return {
        order: {
          state: orderState
        },
        postpaid,
        name,
        alternateContactNumber: contactNumber,
        currentContactNumber: contactNumber,
        firstName: first_name,
        middleName: middle_name,
        lastName: last_name,
        dateOfBirth,
        contactNumber,
        contactEmail,
        simType,
        contractType,
        shippingDetails,
        lastShipmentMethod,
        newShipmentMethod,
        allowedShippingMethod,
        formattedDeliveryFee,
        lastDeliveryDetails,
        rescheduleDeliveryFee,
        ...newSelfPickUpSlotDetails,
        ...newDeliveryDetails
      };
    }
  );
